import { useState } from "react";
import { supabase } from "../lib/supabase";
import { useNavigate } from "react-router-dom";
import {
  Lock,
  Mail,
  Loader2,
  ArrowRight,
  Layers,
  ChevronLeft,
  ShieldAlert,
  UserCheck,
} from "lucide-react";
import { toast } from "react-hot-toast";

export default function StudentLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      toast.error(error.message);
      setLoading(false);
    } else {
      toast.success("Welcome to ProTech Partner Portal");
      setLoading(false);
      navigate("/dashboard");
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-12 bg-[#faf9f6] font-sans">
      {/* --- Left Column: Luxury Split-Screen Hero Image (Desktop Only) --- */}
      <div className="hidden lg:block lg:col-span-5 relative overflow-hidden bg-slate-900">
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-900/40 to-transparent z-10"></div>
        <img
          src="https://images.unsplash.com/photo-1535732759880-bbd5c7265e3f?q=80&w=1964&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
          alt="Premium real estate infrastructure development layout"
          className="absolute inset-0 w-full h-full object-cover transform hover:scale-105 transition-transform duration-700 opacity-85"
        />

        {/* Soft Aesthetic Branding Overlap */}
        <div className="absolute bottom-12 left-12 right-12 z-20 text-white space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8  rounded flex items-center justify-center border border-white/10">
              <img
                src="/protech.png"
                alt="Logo"
                className="w-full h-full object-cover"
              />
            </div>
            <span className="text-xs font-black tracking-widest uppercase text-stone-200">
              PRO<span className="text-emerald-400">TECH</span> CONSTRUCTION
            </span>
          </div>
          <h2 className="text-2xl font-serif text-white leading-tight">
            Managing High-Value Asset Ecosystems.
          </h2>
          <p className="text-xs text-stone-300 max-w-sm leading-relaxed font-light">
            Access secure blueprint maps, client portfolios, mutational property
            titles, and structural deployment logs instantly.
          </p>
        </div>
      </div>

      {/* --- Right Column: Crisp Luxury Form Panel --- */}
      <div className="col-span-1 lg:col-span-7 flex flex-col justify-between p-6 sm:p-12 md:p-20 relative bg-[#faf9f6]">
        {/* Top Header Row */}
        <div className="flex items-center justify-between w-full mb-12 lg:mb-0">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-stone-400 hover:text-emerald-800 transition-colors font-bold text-xs uppercase tracking-wider group"
          >
            <ChevronLeft
              size={16}
              className="group-hover:-translate-x-1 transition-transform"
            />
            Back to Public Website
          </button>
        </div>

        {/* Center Card Content */}
        <div className="w-full max-w-md mx-auto my-auto py-8">
          <div className="text-center lg:text-left mb-10">
            {/* Logo Group */}
            <div className="flex items-center justify-center lg:justify-start gap-2.5 mb-4">
              <div className="w-9 h-9 rounded flex items-center justify-center text-white font-bold shadow-sm">
                <img
                  src="/protech.png"
                  alt="Logo"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex flex-col text-left">
                <span className="text-sm font-black tracking-widest text-slate-900 uppercase leading-none">
                  PRO<span className="text-emerald-700">TECH</span>
                </span>
                <span className="text-[8px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">
                  CONSTRUCTION ENTERPRISES
                </span>
              </div>
            </div>

            <h1 className="text-2xl font-serif text-slate-900 tracking-wide mb-2">
              Partner Enterprise Login
            </h1>

            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 mt-3">
              <span className="text-[10px] font-bold bg-stone-100 text-slate-700 px-2.5 py-1 rounded border border-stone-200 tracking-wider uppercase flex items-center gap-1 shadow-2xs">
                <UserCheck size={12} className="text-emerald-700" /> Authorized
                Access
              </span>
              <span className="text-[10px] font-bold text-stone-400 tracking-wide uppercase flex items-center gap-1">
                <ShieldAlert size={12} className="text-amber-700" /> Personnel
                Vault
              </span>
            </div>
          </div>

          <form onSubmit={handleLogin} className="space-y-5">
            {/* --- Email Input --- */}
            <div className="space-y-1.5 text-left">
              <label className="block text-[10px] font-bold uppercase tracking-widest text-stone-400 ml-1">
                Work Email Address
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-stone-400 group-focus-within:text-emerald-800 transition-colors">
                  <Mail size={16} />
                </div>
                <input
                  type="email"
                  required
                  className="block w-full pl-12 pr-4 py-3.5 bg-white border border-stone-200 text-slate-800 text-sm rounded-xl focus:border-slate-400 transition-all outline-none font-medium shadow-2xs"
                  placeholder="username@protech.infra"
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            {/* --- Password Input --- */}
            <div className="space-y-1.5 text-left">
              <div className="flex justify-between items-center px-1">
                <label className="block text-[10px] font-bold uppercase tracking-widest text-stone-400">
                  Security Password
                </label>
                <button
                  type="button"
                  className="text-[11px] font-bold text-emerald-800 hover:text-emerald-900 hover:underline transition-colors"
                >
                  Reset Access?
                </button>
              </div>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-stone-400 group-focus-within:text-emerald-800 transition-colors">
                  <Lock size={16} />
                </div>
                <input
                  type="password"
                  required
                  className="block w-full pl-12 pr-4 py-3.5 bg-white border border-stone-200 text-slate-800 text-sm rounded-xl focus:border-slate-400 transition-all outline-none font-medium shadow-2xs"
                  placeholder="••••••••"
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>

            {/* --- Submit Button --- */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-slate-800 hover:bg-slate-900 text-white font-medium py-3.5 px-4 rounded-xl shadow-xs transition-all flex items-center justify-center gap-2 group disabled:opacity-70 disabled:cursor-not-allowed uppercase text-xs tracking-widest mt-2"
            >
              {loading ? (
                <Loader2 className="animate-spin text-emerald-200" size={18} />
              ) : (
                <>
                  Secure Gateway Login
                  <ArrowRight
                    size={14}
                    className="group-hover:translate-x-0.5 transition-transform text-emerald-300"
                  />
                </>
              )}
            </button>
          </form>
        </div>

        {/* Bottom Footer Section */}
        <div className="w-full mt-12 lg:mt-0 pt-6 border-t border-stone-200/60 text-center lg:text-left flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-stone-400 font-medium">
          <p>
            Need assistance?
            <button
              onClick={() => navigate("/contact")}
              className="text-emerald-800 hover:underline hover:text-emerald-900 ml-1 font-bold transition-all"
            >
              Contact Institutional Desk
            </button>
          </p>
          <p className="text-[10px] uppercase tracking-wider text-stone-400/80">
            Encrypted Connection • Site ID: PT-ENT-2026
          </p>
        </div>
      </div>
    </div>
  );
}
